/*
  KLS Personalization — behaviour for blocks/kls-personalization.liquid.

  Shared by the product page (the block loads it) and the quick-add modal
  (snippets/kls-quick-add-styles.liquid loads it and calls
  window.klsPersonalization.init(dialog) whenever the modal's content is
  morphed). Everything in init() is idempotent, so it is safe to call it
  repeatedly on the same markup: listeners are bound once per control,
  re-pointing the form association and re-applying the word count are
  cheap, and nothing here writes to the DOM unless a value actually
  changes (the modal watches for mutations and would otherwise loop).

  Line item properties only reach the cart when the input is
  form-associated with the buy-buttons <form>. Horizon's form id is
  BuyButtons-ProductForm-<section id>, and the inputs carry that in their
  form="" attribute from the server. One wrinkle: form="" resolves to the
  FIRST element in the document with that id, so when the quick-add modal
  opens on a product page (recommendations) its form — a clone of the
  page's — would lose to the page's form. init() renames the modal's form
  and re-points its inputs in that case.
*/
(function () {
  var PROPS = 'input[name^="properties["], textarea[name^="properties["], select[name^="properties["]';

  // Which "Word Count" value is currently selected in this section's variant
  // picker (radio buttons or a dropdown). Falls back to the server-rendered value.
  function selectedWordCount(root, section) {
    var optionName = root.dataset.wordOption;
    if (!optionName) return null;
    var prefix = optionName + '-';
    var radios = section.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].checked && radios[i].name.indexOf(prefix) === 0) return parseInt(radios[i].value, 10) || 0;
    }
    var selects = section.querySelectorAll('select');
    for (var j = 0; j < selects.length; j++) {
      if (selects[j].name.indexOf(prefix) === 0) return parseInt(selects[j].value, 10) || 0;
    }
    return parseInt(root.dataset.wordInitial, 10) || 0;
  }

  function setText(el, text) {
    if (el && el.textContent !== String(text)) el.textContent = text;
  }

  // Show/require only the first N word boxes; hidden ones are disabled so
  // they are neither validated nor submitted.
  function applyWordCount(root, section) {
    if (!root.dataset.wordOption) return;
    var count = selectedWordCount(root, section);
    root.querySelectorAll('[data-word-index]').forEach(function (field) {
      var on = parseInt(field.dataset.wordIndex, 10) <= count;
      var control = field.querySelector('[data-kls-pz-input]');
      field.classList.toggle('kls-pz-field--off', !on);
      control.disabled = !on;
      control.required = on && !control.hasAttribute('data-kls-pz-optional');
      if (!on) {
        control.value = '';
        control.classList.remove('is-invalid');
        setText(field.querySelector('[data-kls-pz-count]'), '0');
      }
    });
    var error = root.querySelector('[data-kls-pz-error]');
    if (error && !root.querySelector('.is-invalid')) error.hidden = true;
  }

  // The nearest thing that contains both the boxes and the buy form: the
  // product section on the product page, the <dialog> in the quick-add modal.
  function sectionFor(root) {
    return root.closest('.shopify-section, dialog') || document;
  }

  function bindForm(root, section) {
    var form = section.querySelector('form[data-type="add-to-cart-form"], form[action*="/cart/add"]')
      || document.getElementById(root.dataset.formId);
    if (!form) return;

    // NB: the form contains <input name="id">, which shadows form.id in JS,
    // so always go through the attribute.
    var formId = form.getAttribute('id') || root.dataset.formId;
    if (document.getElementById(formId) !== form) {
      // Another form (the product page's) owns this id; give ours its own.
      formId = formId + '-' + (section.tagName === 'DIALOG' ? 'quick-add' : Math.random().toString(36).slice(2, 7));
    }
    if (form.getAttribute('id') !== formId) form.setAttribute('id', formId);

    // Attach every line item property input in this section to the real
    // product form, including ones from other blocks that point at a stale id.
    section.querySelectorAll(PROPS).forEach(function (el) {
      if (!form.contains(el) && el.getAttribute('form') !== formId) el.setAttribute('form', formId);
    });
  }

  function bindControls(root) {
    var error = root.querySelector('[data-kls-pz-error]');

    root.querySelectorAll('[data-kls-pz-input]').forEach(function (control) {
      var count = control.parentElement.querySelector('[data-kls-pz-count]');
      function updateCount() { setText(count, control.value.length); }
      updateCount();
      if (control._klsBound) return;
      control._klsBound = true;

      function clearInvalid() {
        updateCount();
        control.classList.remove('is-invalid');
        if (error && !root.querySelector('.is-invalid')) error.hidden = true;
      }
      control.addEventListener('input', clearInvalid);
      control.addEventListener('change', clearInvalid);

      // Fired by the browser's constraint validation when the form submits
      // (or Horizon calls checkValidity). Swap the native bubble for our message.
      control.addEventListener('invalid', function (e) {
        e.preventDefault();
        var firstInvalid = root.querySelector('.is-invalid');
        control.classList.add('is-invalid');
        if (error) error.hidden = false;
        if (!firstInvalid) {
          control.focus({ preventScroll: true });
          control.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      });
    });
  }

  function bindWordCount(root, section) {
    if (!root.dataset.wordOption) return;
    applyWordCount(root, section);
    if (section._klsPzChange) return;
    section._klsPzChange = true;
    // Horizon swaps the variant picker markup after a change, so wait a tick.
    // Roots are looked up fresh each time because the modal morphs its content.
    section.addEventListener('change', function (e) {
      if (e.target.matches('input[type="radio"], select') && !e.target.hasAttribute('data-kls-pz-input')) {
        setTimeout(function () {
          section.querySelectorAll('[data-kls-personalization]').forEach(function (r) { applyWordCount(r, section); });
        }, 60);
      }
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-kls-personalization]').forEach(function (root) {
      var section = sectionFor(root);
      bindForm(root, section);
      bindControls(root);
      bindWordCount(root, section);
    });
  }

  // Blank optional property inputs would be sent as empty "Word 2: " lines.
  // Drop them from the submission only.
  function skipBlankProperties(e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    var formId = form.getAttribute('id');
    var candidates = Array.prototype.slice.call(form.querySelectorAll('[name^="properties["]'));
    if (formId) {
      candidates = candidates.concat(Array.prototype.slice.call(
        document.querySelectorAll('[form="' + formId + '"][name^="properties["]')
      ));
    }
    var blanks = candidates.filter(function (el) { return !el.disabled && !String(el.value || '').trim(); });
    blanks.forEach(function (el) { el.disabled = true; });
    if (blanks.length) {
      setTimeout(function () { blanks.forEach(function (el) { el.disabled = false; }); }, 0);
    }
  }

  if (window.klsPersonalization) return; // loaded twice (block + modal)
  window.klsPersonalization = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function () { init(); });
  document.addEventListener('submit', skipBlankProperties, true);
})();
